import { logger } from '@/lib/api/logger';
import { mobx } from '@/lib/mobx';
import { getExpressCalculations } from '@/helpers/getExpressCalculations';
import { LS } from '@/helpers/localStorageHelper';
import { hostname, shadowModeParams } from '@/config';

/**
 * Object for dataLayer actions.
 */
enum dataLayerActions {
  ADD_BET = 'add_to_cart',
  REMOVE_BET = 'remove_from_cart',
  PLACE_BET = 'purchase',
  ERROR = 'error_occured',
  BEGIN_CHECKOUT = 'begin_checkout',
  REFUND = 'refund',
}

declare global {
  interface Window {
    dataLayerTurboSportsbook?: any[];
  }
}

/**
 * Basic interface for a dataLayer event object.
 */
interface DataLayerEvent {
  event?: string;
  [key: string]: any;
}

/**
 * Interface for the setup event.
 */
interface SetupEvent extends DataLayerEvent {
  action: 'setup';
  uid: string;
  state: string;
  currency: string;
  isQuickBet: boolean;
  theme: string;
  formatOdds: string;
  favorite: any[];
  version?: string;
  sid: string;
  fv: boolean;
}

/**
 * Interface for a bet.
 */
export interface Bet {
  id: string;
  trackMarks: string[];
  stake: number;
  name: string;
  marketName: string;
  price: number;
  eventName: string;
  isBetBuilder?: boolean;
  outcomes?: any[];
  betBuilderOutcomes?: Array<{
    outcomeId?: string;
    id?: string;
    name?: string;
    marketName?: string;
    price?: number;
    eventName?: string;
  }>;
}

/**
 * Interface for bet data to be pushed to the dataLayer.
 */
interface BetData {
  /**
   * Outcome id
   */
  item_id: string;

  /**
   * Track marks list
   */
  item_name: string[];

  /**
   * Bet amount
   */
  price: number;

  /**
   * Always equal to 1
   */
  quantity: number;

  /**
   * Outcome name
   */
  item_category: string;

  /**
   * Market name
   */
  item_category2: string;

  /**
   * bet.price
   */
  item_category3: number;

  /**
   * Event name
   */
  item_variant: string;

  /**
   * Is this a BetBuilder item
   */
  isBetBuilder: boolean;

  /**
   * Outcomes for BetBuilder, null for regular bet
   */
  outcomes: any[] | null;
}

/**
 * Interface for ecommerce parameters.
 */
interface EcommerceParameters {
  bet_type: string;
  method: string;
}

/**
 * Interface for an ecommerce event.
 */
interface EcommerceEvent extends DataLayerEvent {
  action: string;
  parameters: EcommerceParameters;
  ecommerce: {
    currency: string;
    transaction_id?: string | undefined;
    error_code?: string | undefined;
    value: string | undefined;
    items: BetData[];
  };
}

/**
 * Interface for the parameters of the pushBetInfo method.
 */
export interface PushBetInfoParams {
  action: 'add_to_cart' | 'remove_from_cart' | 'purchase' | 'begin_checkout' | 'refund';
  bets?: Bet[];
  currentBetType?: string;
  isBetAmountWasEdited?: boolean;
  betslipTabName?: string;
  transactionId?: string;
  isRestoredFromLS?: boolean;
  errorCode?: number;
}

/**
 * The Analytics class provides methods to work with Google Tag Manager (GTM) and the dataLayer.
 */
class Analytics {
  private isInitialized: boolean = false;

  private hasBeenSetUp: boolean = false;

  private readonly dataLayerName = 'dataLayerTurboSportsbook' as const;

  private declare gtmPush: (...args: any[]) => void;

  betSlipBets: {
    SINGLEBET: Array<Bet>;
    EXPRESS: Array<Bet>;
  } = {
    EXPRESS: [],
    SINGLEBET: [],
  };

  restoredBetIds: Set<string> = new Set();

  private tabOppositeAlias: { [key: string]: string } = {
    EXPRESS: 'SINGLEBET',
    SINGLEBET: 'EXPRESS',
  };

  private betslipTabsAliases: { [key: string]: string } = {
    EXPRESS: 'combo',
    SINGLEBET: 'single_bet',
  };

  /**
   * TODO: this is a temporary flag for testing purposes. Remove in future
   */
  gtmLoaded = false;

  constructor() {
    // Initialize the dataLayer array in the appropriate context.
    const dataLayer = window[this.dataLayerName] || [];
    window[this.dataLayerName] = dataLayer;
    this.gtmPush = (...args: any[]) => {
      dataLayer.push(...args);
    };
    this.gtmPush({ event: 'gtm.js', 'gtm.start': Date.now() });

    const scriptId = 'sportsbook-gtm';

    if (!document.getElementById(scriptId)) {
      try {
        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.id = scriptId;
        gtmScript.src = `https://${hostname}/gmetrics/?id=&l=${this.dataLayerName}`;
        gtmScript.onload = () => {
          this.gtmLoaded = true;
        };
        gtmScript.onerror = () => logger.log('Error loading gtm script');
        document.head.appendChild(gtmScript);
      } catch (error) {
        if (error instanceof Error) {
          logger.error('Cannot inject gtm script', { error });
        }
      }
    }
  }

  private getCurrentBetslipTab(betsLength: number): string {
    const { duplicates, comboOutrights, isBlockingCombo, currentTab } = mobx.betslip;
    const { isShareBetEnterCode = false } = mobx.shareBet || {};
    const savedTab = LS.get('esBetslipTab');

    if (betsLength > 1) {
      if (!duplicates.length && !comboOutrights.length) {
        let tab = 'EXPRESS';

        if (isShareBetEnterCode) {
          tab = currentTab;
        }

        if (isBlockingCombo) {
          tab = 'SINGLEBET';
        }

        return isShareBetEnterCode ? tab : savedTab || tab;
      } else {
        return 'SINGLEBET';
      }
    }
    return 'SINGLEBET';
  }

  /**
   * Initializes Google Analytics.
   */
  init(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    const url = new URL(shadowModeParams.host || window.location.href);
    url.searchParams.delete('token');

    this.gtmPush({
      action: 'init',
      cid: shadowModeParams.cid || url.searchParams.get('cid') || LS.get('brand'),
      event: 'ga4_interaction',
      lang: shadowModeParams.lang || url.searchParams.get('locale') || LS.get('locale'),
      safe_url: url.href,
      subpartner_id:
        shadowModeParams.sub_partner_id ||
        url.searchParams.get('sub_partner_id') ||
        LS.get('subPartnerId'),
      theme: shadowModeParams.theme || url.searchParams.get('theme') || LS.get('color_theme'),
    });
  }

  /**
   * Push setup method to GTM with extra data about user
   */
  setup(): void {
    if (this.hasBeenSetUp) {
      return;
    }

    this.hasBeenSetUp = true;

    const {
      ui: { isShowQuickBets, theme, oddsFormat },
      profile: {
        user: { id: uid },
        currency: { name: currencyName },
        favorite: { tournaments: favorites },
      },
      auth: { demo, token },
    } = mobx;

    let state = 'demo';
    const fv = Analytics.isFirstVisit();
    const favoriteTournaments = favorites || [];

    if (!demo) {
      state = token ? 'paid' : 'guest';
    }

    const setupEvent: SetupEvent = {
      action: 'setup',
      currency: currencyName,
      favorite: favoriteTournaments.slice(-10),
      formatOdds: oddsFormat,
      fv,
      isQuickBet: isShowQuickBets,
      state,
      theme,
      uid,
      version: process.env.VERSION,
    };

    this.push(setupEvent);
  }

  /**
   * Checks whether it is the user's first visit.
   *
   * @returns {boolean} Returns true if this is the first visit, otherwise false.
   */
  private static isFirstVisit(): boolean {
    if (!LS.get('nfv')) {
      LS.set('nfv', 'true');

      return true;
    }

    return false;
  }

  /**
   * Pushes data to the appropriate dataLayer.
   *
   * @param params - The event data to push.
   *
   * @returns True if push succeeded, false otherwise.
   */
  push(params: DataLayerEvent = {}, isCustomEventType: boolean = false): boolean {
    const currentParams: DataLayerEvent = { ...params };

    if (!isCustomEventType) {
      currentParams.event = 'ga4_interaction';
    }

    // Clear previous parameters data.
    this.gtmPush({ parameters: null });

    this.gtmPush(currentParams);

    return true;
  }

  /**
   * Pushes bet information to the dataLayer.
   *
   * @param {PushBetInfoParams} options - Parameters containing bet information.
   * @returns {void}
   */
  pushBetInfo({
    action,
    bets = [],
    currentBetType = 'default',
    isBetAmountWasEdited = false,
    betslipTabName = '',
    transactionId = '',
    errorCode,
    isRestoredFromLS = false,
  }: PushBetInfoParams): void {
    try {
      const { currentTab } = mobx.betslip;
      const betsLength: number = bets.length;
      const isCurrentBetTypeDefault = currentBetType === 'default';
      const isRemoveBetFromBetslip =
        isCurrentBetTypeDefault && action === dataLayerActions.REMOVE_BET;
      const currentBetslipTab = isRemoveBetFromBetslip
        ? betslipTabName || currentTab
        : betslipTabName || this.getCurrentBetslipTab(betsLength);
      const isExpressByCurrentTab = currentBetslipTab === 'EXPRESS';

      if (isRestoredFromLS) {
        // If a “bet” is restored from local storage when the page loads,
        // do not add it to analytics because it was added during the previous session.
        bets.forEach(({ id }) => {
          this.restoredBetIds.add(id);
        });
        return;
      }

      // Clear previous ecommerce data.
      this.push({ ecommerce: null }, true);

      if (isCurrentBetTypeDefault && action === dataLayerActions.REMOVE_BET) {
        this.betSlipBets[currentBetslipTab] = this.betSlipBets[currentBetslipTab].filter(
          ({ id: prevBetId }) => !bets.some(({ id }) => prevBetId === id),
        );
      }

      if (isCurrentBetTypeDefault && action === dataLayerActions.BEGIN_CHECKOUT) {
        const neighboringBtslipTab = this.tabOppositeAlias[betslipTabName];
        const neighboringBtslipTabBets = this.betSlipBets[neighboringBtslipTab];
        const betsFromAnalitycsByCurrentTab = this.betSlipBets[betslipTabName];

        if (neighboringBtslipTabBets?.length) {
          this.pushBetInfo({
            action: dataLayerActions.REMOVE_BET,
            bets: neighboringBtslipTabBets,
            betslipTabName: neighboringBtslipTab,
          });
        }

        const betsToAddToCart = bets.filter(
          ({ id }) => !betsFromAnalitycsByCurrentTab.some(({ id: currentId }) => id === currentId),
        );
        if (betsToAddToCart.length) {
          this.pushBetInfo({
            action: dataLayerActions.ADD_BET,
            bets: betsToAddToCart,
            betslipTabName,
          });
        }

        Object.keys(this.betSlipBets).forEach((tab) => {
          this.betSlipBets[tab] = [];
        });
      }

      const betType: string = isCurrentBetTypeDefault
        ? this.betslipTabsAliases[currentBetslipTab]
        : currentBetType;
      const method: string = isBetAmountWasEdited ? 'manual' : 'auto';
      const isCombo = isExpressByCurrentTab || currentBetType === 'turbo_combo';

      const parameters = {
        bet_type: betType,
        combo_booster: undefined,
        method,
        total_odd: undefined,
      };

      const betsData: BetData[] = bets.map((bet: Bet) => {
        const isBetBuilder = Boolean(bet.isBetBuilder);
        const outcomes =
          isBetBuilder && bet.betBuilderOutcomes && bet.betBuilderOutcomes.length > 0
            ? bet.betBuilderOutcomes.map((outcome: any) => ({
                item_id: outcome.outcomeId || '',
                item_marketName: outcome.marketName || '',
                item_name: outcome.name || '',
                item_pidLid: outcome.pidLid || '',
              }))
            : null;

        return {
          isBetBuilder,
          item_category: bet.name,
          item_category2: bet.marketName,
          item_category3: bet.price,
          item_id: bet.id,
          item_name: bet.trackMarks,
          item_variant: bet.eventName,
          outcomes,
          price: bet.stake || 0,
          quantity: 1,
        };
      });

      const ecommerce = {
        currency: mobx.profile.currency.name,
        error_code: errorCode || undefined,
        items: betsData,
        transaction_id: transactionId || undefined,
        value: undefined,
      };

      if (isCombo && action !== dataLayerActions.REMOVE_BET) {
        const { isShowComboBooster } = mobx.betslip;

        const {
          boostLevels: {
            currentBoost: { odd: currentBoostOdd },
          },
          expressOdd,
        } = getExpressCalculations(bets, isShowComboBooster);

        if (isShowComboBooster) {
          parameters.combo_booster = currentBoostOdd;
        }
        parameters.total_odd = expressOdd;
      }

      if (action === dataLayerActions.PLACE_BET) {
        ecommerce.value = isCombo
          ? bets[0].stake
          : bets.reduce((acc, { stake }) => acc + +stake, 0);
      }

      const paramsToPush: EcommerceEvent = {
        action,
        ecommerce,
        parameters,
      };

      this.push(paramsToPush);
    } catch (error) {
      logger.error('Error pushing bet info to dataLayer', { action, bets, error });
    }
  }

  pushErrorNotification(message: string) {
    const data: DataLayerEvent = {
      action: dataLayerActions.ERROR,
      parameters: {
        object: message,
      },
    };
    this.push(data);
  }
}

const analytics = new Analytics();

export { dataLayerActions, analytics };
